import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useLanguage } from '../i18n/LanguageContext';
import { useTheme } from '@mui/material/styles';

interface HistoryPoint {
  ts: number;
  value: number;
}

interface Props {
  open: boolean;
  sensorId: string | null;
  sensorName: string;
  sensorUnit: string;
  sourceUrl: string;
  getAuthHeader: () => object;
  onClose: () => void;
}

const HOUR_OPTIONS = [
  { label: '1h',  hours: 1 },
  { label: '6h',  hours: 6 },
  { label: '24h', hours: 24 },
  { label: '7d',  hours: 168 },
];

const CW = 300, CH = 130;
const PL = 46, PR = 12, PT = 10, PB = 28;
const IW = CW - PL - PR;
const IH = CH - PT - PB;

const LineChart: React.FC<{ points: HistoryPoint[]; unit: string; color: string }> = ({
  points, unit, color,
}) => {
  if (points.length < 2) return null;

  const minTs = points[0].ts;
  const maxTs = points[points.length - 1].ts;
  const vals  = points.map(p => p.value);
  const minV  = Math.min(...vals);
  const maxV  = Math.max(...vals);
  const rangeV = maxV === minV ? 1 : maxV - minV;

  const cx = (ts: number) => ((ts - minTs) / (maxTs - minTs || 1)) * IW;
  const cy = (v: number)  => IH - ((v - minV) / rangeV) * IH;

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${cx(p.ts).toFixed(1)},${cy(p.value).toFixed(1)}`)
    .join(' ');
  const areaPath = `${linePath} L${IW.toFixed(1)},${IH} L0,${IH} Z`;

  const yTicks = [maxV, (minV + maxV) / 2, minV];
  const midTs  = (minTs + maxTs) / 2;
  const useDate = maxTs - minTs > 86400;
  const fmt = (ts: number) => {
    const d = new Date(ts * 1000);
    return useDate
      ? `${d.getMonth() + 1}/${d.getDate()} ${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`
      : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <svg width="100%" viewBox={`0 0 ${CW} ${CH}`} style={{ display: 'block' }}>
      <defs>
        <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <g transform={`translate(${PL},${PT})`}>
        {yTicks.map((v, i) => (
          <line key={i} x1={0} y1={cy(v)} x2={IW} y2={cy(v)}
            stroke="#888" strokeOpacity="0.2" strokeDasharray="3,3" />
        ))}
        <path d={areaPath} fill="url(#sg)" />
        <path d={linePath} fill="none" stroke={color} strokeWidth={1.8}
          strokeLinecap="round" strokeLinejoin="round" />
        {yTicks.map((v, i) => (
          <text key={i} x={-5} y={cy(v) + 4} textAnchor="end" fontSize={9} fill="#888">
            {v.toFixed(1)}{unit}
          </text>
        ))}
        <text x={0}      y={IH + 18} textAnchor="start"  fontSize={9} fill="#888">{fmt(minTs)}</text>
        <text x={IW / 2} y={IH + 18} textAnchor="middle" fontSize={9} fill="#888">{fmt(midTs)}</text>
        <text x={IW}     y={IH + 18} textAnchor="end"    fontSize={9} fill="#888">{fmt(maxTs)}</text>
      </g>
    </svg>
  );
};

export const SensorHistoryDialog: React.FC<Props> = ({
  open, sensorId, sensorName, sensorUnit, sourceUrl, getAuthHeader, onClose,
}) => {
  const { t } = useLanguage();
  const theme  = useTheme();
  const color  = theme.palette.primary.main;
  const [hours,   setHours]   = useState(24);
  const [loading, setLoading] = useState(false);
  const [points,  setPoints]  = useState<HistoryPoint[]>([]);

  useEffect(() => {
    if (!open || !sensorId) return;
    setLoading(true);
    const base = sourceUrl.replace(/\/$/, '');
    const url  = `${base}/sensors/history?id=${encodeURIComponent(sensorId)}&hours=${hours}`;
    const controller = new AbortController();
    fetch(url, { headers: getAuthHeader(), signal: controller.signal })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then((data: HistoryPoint[]) => setPoints(data))
      .catch(() => setPoints([]))
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [open, sensorId, hours, sourceUrl, getAuthHeader]);

  const vals = points.map(p => p.value);
  const min  = vals.length ? Math.min(...vals) : null;
  const max  = vals.length ? Math.max(...vals) : null;
  const avg  = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
        <span>{sensorName} — {t.sensorHistory}</span>
        <IconButton size="small" aria-label={t.a11yClose} onClick={onClose}><Close fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <ButtonGroup size="small">
            {HOUR_OPTIONS.map(o => (
              <Button
                key={o.hours}
                variant={hours === o.hours ? 'contained' : 'outlined'}
                onClick={() => setHours(o.hours)}
              >
                {o.label}
              </Button>
            ))}
          </ButtonGroup>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : points.length < 2 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">{t.sensorHistoryNone}</Typography>
          </Box>
        ) : (
          <>
            <LineChart points={points} unit={sensorUnit} color={color} />
            <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 1.5 }}>
              {[['Min', min], ['Avg', avg], ['Max', max]].map(([label, val]) => (
                <Box key={label as string} sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {label}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {(val as number).toFixed(1)}{sensorUnit}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
